import React, { useState } from 'react'
import { 
  Edit, Package, Euro, BarChart3, QrCode,
  Calendar, User, AlertTriangle, TrendingUp,
  Download, Printer, Archive, Clock
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../ui/Button'
import Modal from '../Modal'

import { Produto } from '../../types'
import { 
  formatarValor,
  formatarQuantidade,
  getStatusEstoque,
  formatarPorcentagem,
  calcularMargemLucro,
  getTipoMovimentacaoConfig
} from '../../services/produtos'
import movimentacoesService from '../../services/movimentacoes'

interface ProdutoDetailsProps {
  produto: Produto
  onEdit: () => void
  onMovimentar: () => void
  onClose: () => void
}

const ProdutoDetails: React.FC<ProdutoDetailsProps> = ({
  produto,
  onEdit,
  onMovimentar,
  onClose
}) => {
  const [showQRCode, setShowQRCode] = useState(false)

  // Query para buscar movimentações recentes
  const { data: movimentacoes } = useQuery({
    queryKey: ['movimentacoes', produto.id],
    queryFn: () => movimentacoesService.getByProduto(produto.id, 10)
  })

  const statusEstoque = getStatusEstoque(produto)
  const margemCalculada = calcularMargemLucro(produto.precoCusto, produto.precoVenda)

  const handleExportarQR = () => {
    // Implementar exportação do QR Code
    console.log('Exportar QR Code')
  }

  const handleImprimirEtiqueta = () => {
    // Implementar impressão de etiqueta
    console.log('Imprimir etiqueta')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Package className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {produto.nome}
            </h2>
            <p className="text-gray-600">SKU: {produto.codigoInterno}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowQRCode(true)}
            leftIcon={<QrCode className="h-4 w-4" />}
          >
            QR Code
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onMovimentar}
            leftIcon={<BarChart3 className="h-4 w-4" />}
          >
            Movimentar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            leftIcon={<Edit className="h-4 w-4" />}
          >
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações básicas */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informações do Produto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Nome</label>
                <p className="text-sm text-gray-900">{produto.nome}</p>
              </div>
              
              {produto.descricao && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500">Descrição</label>
                  <p className="text-sm text-gray-900">{produto.descricao}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-500">Categoria</label>
                <p className="text-sm text-gray-900">{typeof produto.categoria === 'object' && produto.categoria?.nome ? produto.categoria.nome : produto.categoria}</p>
              </div>

              {produto.marca && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Marca</label>
                  <p className="text-sm text-gray-900">{typeof produto.marca === 'object' && produto.marca?.nome ? produto.marca.nome : produto.marca}</p>
                </div>
              )}
              
              {produto.modelo && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Modelo</label>
                  <p className="text-sm text-gray-900">{produto.modelo}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-500">SKU</label>
                <p className="text-sm text-gray-900 font-mono">{produto.codigoInterno}</p>
              </div>
              
              {produto.codigoBarras && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Código de Barras</label>
                  <p className="text-sm text-gray-900 font-mono">{produto.codigoBarras}</p>
                </div>
              )}
              
              {produto.localizacao && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Localização</label>
                  <p className="text-sm text-gray-900">{produto.localizacao}</p>
                </div>
              )}
            </div>
          </div>

          {/* Preços e margens */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Preços e Margens
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Preço de Custo</label>
                <p className="text-lg font-medium text-gray-900">{formatarValor(produto.precoCusto)}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500">Preço de Venda</label>
                <p className="text-lg font-medium text-gray-900">{formatarValor(produto.precoVenda)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500">Margem de Lucro</label>
                <p className="text-lg font-medium text-green-600">
                  {formatarPorcentagem(margemCalculada)}
                </p>
              </div>

              <div className="md:col-span-3 bg-blue-50 p-3 rounded border border-blue-200">
                <div className="text-sm text-blue-800">
                  <div>Lucro por unidade: {formatarValor(produto.precoVenda - produto.precoCusto)}</div>
                  <div>Valor total em stock: {formatarValor(produto.stockAtual * produto.precoCusto)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Histórico de movimentações */}
          {movimentacoes && movimentacoes.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Movimentações Recentes
              </h3>
              <div className="space-y-3">
                {movimentacoes.map((mov) => {
                  const config = getTipoMovimentacaoConfig(mov.tipo)
                  
                  return (
                    <div key={mov.id} className="flex items-start gap-3 bg-white p-3 rounded border">
                      <div className="flex-shrink-0 text-lg">{config.icon}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`font-medium ${
                            config.color === 'green' ? 'text-green-600' :
                            config.color === 'red' ? 'text-red-600' :
                            'text-blue-600'
                          }`}>
                            {config.label}
                          </span>
                          <span className="text-gray-500">
                            por {mov.usuario?.nome || 'Sistema'}
                          </span>
                          <span className="text-gray-400">
                            {new Date(mov.createdAt).toLocaleDateString('pt-PT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Quantidade: {formatarQuantidade(mov.quantidade, mov.tipo)} |
                          Stock: {formatarQuantidade(mov.estoqueAnterior)} → {formatarQuantidade(mov.estoqueAtual)}
                        </div>
                        {mov.observacoes && (
                          <p className="text-sm text-gray-600 mt-1">{mov.observacoes}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Observações */}
          {produto.observacoes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Observações</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{produto.observacoes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status do stock */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Status do Stock
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Stock Atual</span>
                <span className={`text-lg font-bold ${
                  statusEstoque.color === 'red' ? 'text-red-600' :
                  statusEstoque.color === 'yellow' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {formatarQuantidade(produto.stockAtual)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Stock Mínimo</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatarQuantidade(produto.stockMinimo)}
                </span>
              </div>
              
              <div className="border-t pt-2">
                <div className="flex items-center gap-2">
                  {statusEstoque.color === 'red' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                  {statusEstoque.color === 'yellow' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                  {statusEstoque.color === 'green' && <TrendingUp className="h-4 w-4 text-green-500" />}
                  <span className={`text-sm font-medium ${
                    statusEstoque.color === 'red' ? 'text-red-600' :
                    statusEstoque.color === 'yellow' ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {statusEstoque.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Informações do sistema */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Informações
            </h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-500">Criado em</span>
                <p className="text-sm text-gray-900">
                  {new Date(produto.createdAt).toLocaleDateString('pt-PT')}
                </p>
              </div>
              
              <div>
                <span className="text-xs text-gray-500">Última atualização</span>
                <p className="text-sm text-gray-900">
                  {new Date(produto.updatedAt).toLocaleDateString('pt-PT')}
                </p>
              </div>
              
              <div>
                <span className="text-xs text-gray-500">Status</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${produto.ativo ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm text-gray-900">
                    {produto.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
              
              {produto._count && (
                <div>
                  <span className="text-xs text-gray-500">Movimentações</span>
                  <p className="text-sm text-gray-900">
                    {produto._count.movimentacoes} registros
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ações */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Ações</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<QrCode className="h-4 w-4" />}
                onClick={() => setShowQRCode(true)}
              >
                Visualizar QR Code
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleExportarQR}
              >
                Exportar QR Code
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<Printer className="h-4 w-4" />}
                onClick={handleImprimirEtiqueta}
              >
                Imprimir Etiqueta
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                fullWidth
                leftIcon={<Archive className="h-4 w-4" />}
                onClick={() => {
                  // Implementar arquivamento
                  console.log('Arquivar produto')
                }}
              >
                {produto.ativo ? 'Arquivar' : 'Reativar'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal QR Code */}
      <Modal
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
        title="QR Code do Produto"
        size="sm"
      >
        <div className="text-center space-y-4">
          {produto.qrCode ? (
            <div className="flex justify-center">
              <img 
                src={produto.qrCode} 
                alt="QR Code" 
                className="w-48 h-48 border border-gray-200 rounded"
              />
            </div>
          ) : (
            <div className="w-48 h-48 mx-auto bg-gray-100 border border-gray-200 rounded flex items-center justify-center">
              <div className="text-center text-gray-500">
                <QrCode className="h-12 w-12 mx-auto mb-2" />
                <p className="text-sm">QR Code não disponível</p>
              </div>
            </div>
          )}
          
          <div className="text-sm text-gray-600">
            <p><strong>Produto:</strong> {produto.nome}</p>
            <p><strong>SKU:</strong> {produto.codigoInterno}</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportarQR}
              leftIcon={<Download className="h-4 w-4" />}
            >
              Exportar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImprimirEtiqueta}
              leftIcon={<Printer className="h-4 w-4" />}
            >
              Imprimir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ProdutoDetails